/* Tiny top-of-stack registry so nested Modals don't both handle
   Escape / focus-trap from a single keystroke.

   The standard trick — e.stopPropagation() inside the keydown
   handler — doesn't help when two Modals each attach their own
   listener directly on `document`: both listeners fire on the
   same event target (document), and stopPropagation in one
   doesn't prevent the OTHER document-level listener from firing
   in the same dispatch round. So we register every open Modal
   on a shared stack and have each Modal's keydown short-circuit
   when it's not the topmost entry.

   The stack is mount-order, not z-index order. Modal doesn't own
   proper z-index stacking — see docs/DEFERRED.md → "Modal stacking"
   for the broader context. For the two-modals case (useConfirm
   inside an already-open Modal), mount order = visual order, so
   this produces the right outcome. */

import { IS_PROD, devWarn } from './devWarn';

const _stack: symbol[] = [];

export function pushModal(): symbol {
  const token = Symbol('modal');
  _stack.push(token);
  return token;
}

export function popModal(token: symbol): void {
  const idx = _stack.lastIndexOf(token);
  if (idx === -1) {
    /* Popping a token that isn't on the stack means the lifecycle
       got out of sync — a double-pop, a token from a stale mount
       effect that the StrictMode re-mount replaced, or a stack
       that was cleared out-of-band. Silent noop would hide the
       bug; dev-warn surfaces it without throwing (we don't want
       a stacking glitch to cascade into an unmount error). */
    if (!IS_PROD) devWarn('[JKit.Modal] popModal called with a token not on the stack');
    return;
  }
  _stack.splice(idx, 1);
}

export function isTopmost(token: symbol): boolean {
  return _stack.length > 0 && _stack[_stack.length - 1] === token;
}
